import React, { FC, PropsWithChildren, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { CircleCheck, CircleX, Copy } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ButtonLoadable } from '@/components/ui/button-loadable';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useCopyToClipboard } from '@/hooks/use-copy-to-clipboard';

import { IntegrationServices, IntegrationState } from '../../../../common/types/integrations';
import { guideMap } from '../../helpers/data';
import { sleep } from '../../helpers/sleep';
import { ConnectionFormValues, createConnectionFormSchema } from '../../schemas/connection-schema';
import { useIntegrationStore } from '../../state/use-integration-store';

interface ConnectionModalProps extends PropsWithChildren {
  service: IntegrationServices;
  /** Controlled open state (optional) */
  open?: boolean;
  /** Callback when open state changes (optional) */
  onOpenChange?: (open: boolean) => void;
}

export const ConnectionModal: FC<ConnectionModalProps> = ({
  service,
  children,
  open: controlledOpen,
  onOpenChange,
}) => {
  const { t } = useTranslation(['authenticated.onboarding.integrations']);

  const [_, copy] = useCopyToClipboard();
  const { setIntegrationState } = useIntegrationStore();

  const [internalOpen, setInternalOpen] = useState(false);

  // Use controlled state if provided, otherwise use internal state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  const connectionFormSchema = createConnectionFormSchema(t);
  const form = useForm<ConnectionFormValues>({
    resolver: zodResolver(connectionFormSchema),
    defaultValues: {
      // TODO: Insert the URL according to the service.
      oauth: 'https://app.opsium.io/rest/oauth2-credential/callback',
      clientID: '',
      clientSecret: '',
    },
  });

  const handleSubmit = async (values: ConnectionFormValues) => {
    // TODO: Need to remove. Simulation of data processing.
    await sleep(3000);

    if (service === IntegrationServices.GOOGLE) {
      console.log('Form Values: ', values);

      toast.success(t('toast.integrationSuccess'));
      setIntegrationState(service, IntegrationState.CONNECTED);
    } else {
      form.setError('root', {
        type: 'server',
        message: 'Submission failed. Please try again.',
      });

      toast.error(t('toast.integrationError'));
      setIntegrationState(service, IntegrationState.ERROR_CONNECTION);
    }
  };

  const handleServiceGuide = () => {
    const guideLink = guideMap[service];
    window.open(guideLink, '_blank')?.focus();
  };

  const handleCopy = () => {
    const oauthValue = form.getValues('oauth');

    copy(oauthValue || '')
      .then(() => toast.success(t('toast.copyToClipboard.oauthRedirectUrl.success')))
      .catch(() => toast.error(t('toast.copyToClipboard.oauthRedirectUrl.error')));
  };

  const handleClose = (open: boolean) => {
    form.reset();
    setOpen(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogTrigger asChild>{children}</DialogTrigger>

      <DialogContent className="max-w-md">
        <DialogHeader className="space-y-2">
          <DialogTitle className="mb-2">
            {service} {t('modals.connection.title')}
          </DialogTitle>
        </DialogHeader>

        {form.formState.isSubmitSuccessful && (
          <Alert variant="success">
            <div className="flex items-center gap-1.5">
              <CircleCheck className="h-4 w-4 flex-shrink-0" />

              <AlertDescription className="text-sm/4 font-medium">
                {t('toast.successConnect')}
              </AlertDescription>
            </div>
          </Alert>
        )}

        {form.formState.errors.root && (
          <Alert variant="error">
            <div className="flex items-center gap-1.5">
              <CircleX className="h-4 w-4 flex-shrink-0" />

              <AlertDescription className="text-sm/4 font-medium">
                {t('toast.errorConnect')}
              </AlertDescription>
            </div>
          </Alert>
        )}

        <div className="rounded-lg border border-input bg-primary-foreground p-4 text-xs text-foreground [&>svg]:text-foreground">
          <div className="flex items-center justify-between gap-1.5">
            {service} {t('modals.connection.alert.serviceGuide')}
            <Button type="button" variant="link" size="sm" onClick={handleServiceGuide}>
              {t('modals.connection.alert.openGuide')}
            </Button>
          </div>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4 pt-4">
            <FormField
              control={form.control}
              name="oauth"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.oauth.label')}</FormLabel>

                  <FormControl>
                    <div className="relative">
                      <Input className="truncate pr-9" {...field} />
                      <Copy
                        className="absolute right-3 top-3 h-4 w-4 cursor-pointer transition-opacity hover:opacity-50"
                        onClick={handleCopy}
                      />
                    </div>
                  </FormControl>

                  <FormDescription className="text-xs">
                    {t('fields.oauth.description')}
                  </FormDescription>

                  <FormMessage />
                </FormItem>
              )}
              disabled
            />

            <FormField
              control={form.control}
              name="clientID"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.clientID.label')}*</FormLabel>

                  <FormControl>
                    <Input placeholder={t('fields.clientID.placeholder')} {...field} />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="clientSecret"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.clientSecret.label')}*</FormLabel>

                  <FormControl>
                    <Input
                      type="password"
                      placeholder={t('fields.clientSecret.placeholder')}
                      {...field}
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="pt-4">
              {form.formState.isSubmitSuccessful ? (
                <DialogClose asChild>
                  <Button type="button">{t('actions.close')}</Button>
                </DialogClose>
              ) : (
                <>
                  <DialogClose asChild>
                    <Button type="button" variant="outline">
                      {t('actions.close')}
                    </Button>
                  </DialogClose>

                  <ButtonLoadable isLoading={form.formState.isSubmitting}>
                    {form.formState.isSubmitting
                      ? t('modals.connection.actions.connecting')
                      : t('modals.connection.actions.testConnection')}
                  </ButtonLoadable>
                </>
              )}
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
